import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import axiosClient from 'api/axiosClient';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(() => localStorage.getItem('accessToken'));
  const [loading, setLoading] = useState(() => {
    if (localStorage.getItem('accessToken')) return true;
    const params = new URLSearchParams(window.location.search);
    return params.get('auth') === 'success';
  });
  const loggedOutRef = useRef(false);

  useEffect(() => {
    if (!accessToken) {
      if (loggedOutRef.current) {
        setLoading(false);
        return;
      }
      let cancelled = false;
      const bootstrapFromRefreshCookie = async () => {
        try {
          const { data } = await axiosClient.post('/auth/refresh-token');
          if (cancelled) return;
          const token = data.tokens?.accessToken;
          if (token) {
            localStorage.setItem('accessToken', token);
            setAccessToken(token);
          }
        } catch {
          if (cancelled) return;
          setLoading(false);
        }
      };
      void bootstrapFromRefreshCookie();
      return () => {
        cancelled = true;
      };
    }

    let cancelled = false;
    const fetchMe = async () => {
      setLoading(true);
      try {
        const res = await axiosClient.get('/user/me');
        if (cancelled) return;
        if (res.data.status === 'success' && res.data.user) {
          setUser(res.data.user);
        } else {
          localStorage.removeItem('accessToken');
          setAccessToken(null);
          setUser(null);
        }
      } catch {
        if (cancelled) return;
        localStorage.removeItem('accessToken');
        setAccessToken(null);
        setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    const params = new URLSearchParams(window.location.search);
    if (params.get('auth') === 'success') {
      const url = new URL(window.location.href);
      url.searchParams.delete('auth');
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    }

    void fetchMe();
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  const login = useCallback(async (email, password, recaptchaToken) => {
    const { data } = await axiosClient.post('/user/login', { email, password, recaptchaToken });
    localStorage.setItem('accessToken', data.tokens.accessToken);
    setAccessToken(data.tokens.accessToken);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (tenantName, name, email, password, recaptchaToken, _honeypot = '') => {
    const { data } = await axiosClient.post('/user/register', {
      tenantName,
      name,
      email,
      password,
      recaptchaToken,
      _honeypot
    });
    if (data.emailConfirmationEnabled) {
      return { emailConfirmationEnabled: true, message: data.message };
    }
    if (data.tokens?.accessToken) {
      localStorage.setItem('accessToken', data.tokens.accessToken);
      setAccessToken(data.tokens.accessToken);
      setUser(data.user);
    }
    return data;
  }, []);

  const updateUser = useCallback((patch) => {
    setUser((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const establishSession = useCallback((token, userData) => {
    localStorage.setItem('accessToken', token);
    setAccessToken(token);
    setUser(userData);
  }, []);

  const switchTenant = useCallback(async (tenantId) => {
    const { data } = await axiosClient.post('/auth/switch-tenant', { tenantId });
    const token = data.tokens?.accessToken;
    if (token) {
      localStorage.setItem('accessToken', token);
      setAccessToken(token);
    }
    if (data.user) setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    loggedOutRef.current = true;
    try {
      await axiosClient.post('/auth/logout');
    } catch {
      /* ignore */
    }
    localStorage.removeItem('accessToken');
    setAccessToken(null);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateUser, establishSession, switchTenant }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
